import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';
import { message } from 'antd';
import { jwtDecode } from 'jwt-decode';

const AuthContext = createContext();

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);

  // 根据令牌状态确定认证状态
  const isAuthenticated = !!token;

  useEffect(() => {
    // 验证令牌并获取用户信息
    const validateToken = async () => {
      const storedToken = localStorage.getItem('token');
      
      if (storedToken) {
        try {
          // 验证令牌是否过期
          const decodedToken = jwtDecode(storedToken);
          const currentTime = Date.now() / 1000;
          
          if (decodedToken.exp < currentTime) {
            // 令牌已过期
            logout();
          } else {
            // 令牌有效，设置用户信息
            setToken(storedToken);
            
            // 设置axios默认头部包含认证令牌
            axios.defaults.headers.common['Authorization'] = `Bearer ${storedToken}`;
            
            // 获取用户信息
            try {
              const response = await axios.get('/api/auth/me');
              console.log("用户信息获取成功:", response.data);
              setCurrentUser(response.data);
            } catch (error) {
              console.error('获取用户信息失败:', error);
              // 如果是404错误，尝试从token解析基本用户信息
              if (error.response && error.response.status === 404) {
                console.log("尝试从token解析用户信息");
                // 从token中提取用户名作为基本信息
                const username = decodedToken.sub;
                setCurrentUser({
                  username: username,
                  // 其他字段设为空值或默认值
                  id: null,
                  email: null,
                  fullName: null,
                  roles: []
                });
                console.log("已设置基本用户信息:", username);
              } else {
                // 其他错误则登出
                logout();
              }
            }
          }
        } catch (error) {
          console.error('令牌验证失败:', error);
          logout();
        }
      }
      
      setLoading(false);
    };

    validateToken();
  }, [token]);

  // 登录
  const login = async (username, password) => {
    try {
      const response = await axios.post('/api/auth/login', {
        username,
        password
      });
      
      const { token } = response.data;
      
      // 保存令牌
      localStorage.setItem('token', token);
      setToken(token);
      
      // 设置axios默认头部
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      
      // 获取用户信息
      const userResponse = await axios.get('/api/auth/me');
      setCurrentUser(userResponse.data);
      
      message.success('登录成功！');
      return true;
    } catch (error) {
      console.error('登录失败:', error);
      message.error(error.response?.data?.message || '登录失败，请检查用户名和密码');
      return false;
    }
  };

  // 注册
  const register = async (userData) => {
    try {
      console.log('发送注册请求，数据：', userData);
      
      // 确保数据格式正确
      const requestData = {
        username: userData.username,
        email: userData.email,
        password: userData.password,
        fullName: userData.fullName || null
      };
      
      console.log('格式化后的请求数据:', JSON.stringify(requestData));
      
      // 使用fetch API代替axios尝试发送请求
      const response = await fetch('/api/auth/signup', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(requestData)
      });
      
      console.log('注册响应状态:', response.status);
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error('注册错误响应:', errorText);
        throw new Error(errorText || '注册失败');
      }
      
      const data = await response.json();
      console.log('注册成功响应:', data);
      
      message.success('注册成功！请登录');
      return true;
    } catch (error) {
      console.error('注册失败详情:', error);
      let errorMsg = '注册失败，请稍后再试';
      
      if (error.message) {
        errorMsg = error.message;
      }
      
      message.error(errorMsg);
      return false;
    }
  };

  // 登出
  const logout = () => {
    localStorage.removeItem('token');
    setToken(null);
    setCurrentUser(null);
    delete axios.defaults.headers.common['Authorization'];
    message.success('您已成功退出登录');
  };

  // 提供的上下文值
  const value = {
    currentUser,
    isAuthenticated,
    loading,
    login,
    register,
    logout
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
}; 